/**
 * Request-host-aware resolution of the Identity app's URL.
 *
 * This app is reached on more than one registrable domain (`admin.welbee.co.uk` and
 * `admin.edu-intelligence.ai`), but `REACT_APP_*` values are embedded at build time, so a single
 * bundle held one hardcoded Identity URL.
 *
 * That matters because the session is an HTTP-only cookie scoped to a registrable domain, and
 * `IdentityAuth` exchanges it for a JWT with a credentialed browser fetch. Asking the browser to
 * send an `edu-intelligence.ai` cookie to `identity.welbee.co.uk` does not work — cookies do not
 * cross registrable domains — so the exchange returns 401 and the app looks signed out even though
 * the user has just signed in.
 */

export const DEFAULT_SERVED_DOMAINS = ['welbee.co.uk', 'edu-intelligence.ai'];

/** Strips any port and trailing dot, and lower-cases. Returns '' for unusable input. */
export function normaliseHost(host) {
  if (!host) return '';

  // A proxy chain appends to x-forwarded-host ("real.host, internal.host"); the first entry is
  // the host the browser asked for. Without this the whole string matches no domain and the
  // caller silently falls back.
  const first = String(host).split(',')[0].trim();

  // Bracketed IPv6 keeps its brackets. A bare IPv6 literal ("::1") has more than one colon and no
  // port to strip — splitting on the first colon would mangle it into "".
  const withoutPort = first.startsWith('[')
    ? first.slice(0, first.indexOf(']') + 1)
    : (first.split(':').length > 2 ? first : first.split(':')[0]);

  return withoutPort.trim().replace(/\.$/, '').toLowerCase();
}

/**
 * The served domain `host` belongs to, or undefined.
 *
 * Anchored to a label boundary so `notwelbee.co.uk` never matches `welbee.co.uk`; longest match
 * wins so a more specific domain is not swallowed by a broader one.
 */
export function matchServedDomain(host, servedDomains = DEFAULT_SERVED_DOMAINS) {
  const h = normaliseHost(host);
  if (!h) return undefined;

  return (servedDomains || [])
    .map((d) => normaliseHost(d).replace(/^\./, ''))
    .filter(Boolean)
    .filter((d) => h === d || h.endsWith(`.${d}`))
    .sort((a, b) => b.length - a.length)[0];
}

/**
 * Parses the `REACT_APP_IDENTITY_URLS_BY_DOMAIN` JSON map. Malformed configuration resolves to an
 * empty map so lookups fall back to the build-time default rather than breaking sign-in.
 */
export function parseIdentityUrlsByDomain(raw) {
  if (!raw || !String(raw).trim()) return {};

  // Tolerate a value that still carries its shell/Dockerfile quoting.
  const unquoted = String(raw).trim().replace(/^(['"])([\s\S]*)\1$/, '$2');

  try {
    const parsed = JSON.parse(unquoted);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    const result = {};
    for (const [domain, url] of Object.entries(parsed)) {
      if (typeof url === 'string' && url.trim()) result[normaliseHost(domain).replace(/^\./, '')] = url.trim();
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * The Identity app URL for a browser sitting on `host`. A domain with no configured entry keeps
 * the build-time default, so behaviour on the original domain is unchanged.
 */
export function resolveIdentityUrl(host, urlsByDomain, fallback) {
  const matched = matchServedDomain(host, Object.keys(urlsByDomain || {}));
  const configured = matched ? urlsByDomain[matched] : undefined;

  return (configured && configured.trim()) || fallback;
}
